import React, { useEffect, useMemo, useState } from 'react';
import useServiceAuth from 'auth/useServiceAuth';
// import { fetchDplColleges } from 'apis/dplAPIs';
import { useMutation, useQuery } from '@tanstack/react-query';
import StaticData from '../components/StaticData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faPlusCircle,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { userCheck, userDelete, userGetAll, userSave } from 'apis/adminAPIs';
import { useSelector } from 'react-redux';
import { classNames } from 'utils';
import { Button } from '@asu/components-core';
import { Dropdown, Modal, Toast, ToastContainer } from 'react-bootstrap';
import DropdownCustomToggle from 'components/DropdownCustomToggle';

const roleLabel = {
  '["DEPARTMENT"]': 'Department',
  '["COLLEGE"]': 'College',
  '["PROVOST"]': 'Provost',
  '["DARS"]': 'DARS',
  '["VIEWER"]': 'Viewer',
  '["PRVVIEWER"]': 'PRV Viewer',
};

const Users = () => {
  const { getAccessToken } = useServiceAuth();

  const [asurite, setAsurite] = useState('');
  const [emplid, setEmplid] = useState('');
  const [name, setName] = useState('');
  const [userNotFound, setUserNotFound] = useState(false);
  const [firstUserFound, setFirstUserFound] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDarsColleges, setDarsSelectedColleges] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const [showColleges, setShowColleges] = useState(false);
  const [showDepartments, setShowDepartments] = useState(false);
  const [numberOfDepartments, setNumberOfDepartments] = useState(1);
  const [numberOfDarsColleges, setNumberOfDarsColleges] = useState(1);
  const [lockAddUser, setLockAddUser] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userExists, setUserExists] = useState(false);
  const [statusFilters, setStatusFilters] = useState([]);
  const [collegeFilters, setCollegeFilters] = useState([]);
  const [departmentFilters, setDepartmentFilters] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const collegeList = useSelector((state) => state.dataLists.collegeList);
  const departmentList = useSelector((state) => state.dataLists.departmentList);

  const {
    data: getAllUsersData,
    error: getAllUsersError,
    isError: getAllUsersIsError,
    isSuccess: getAllUsersIsSuccess,
    isPending: getAllUsersIsPending,
    fetchStatus: getAllUsersFetchStatus,
    refetch,
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const token = await getAccessToken();
      return userGetAll({ token });
    },
  });

  const { mutate: checkUser } = useMutation({
    mutationFn: userCheck,
    onSuccess: (data, variables) => {
      const isEmplid = /^\d+$/.test(variables.id);

      const ownerEmplid = isEmplid ? variables.id : data.owner?.emplid;
      const ownerAsurite = isEmplid ? data.primaryAsuriteid : variables.id;

      const names = isEmplid ? data.names : data.owner?.names;

      const nameInfo =
        names && !!Object.keys(names).length ? names.PRF ?? names.PRI : null;

      if (ownerEmplid) setEmplid(ownerEmplid);
      if (isEmplid && ownerAsurite) setAsurite(ownerAsurite);

      if (nameInfo) {
        setName(nameInfo.givenName + ' ' + nameInfo.familyName);
        setFirstUserFound(true);
        setUserNotFound(false);
      } else {
        setName('');
        setUserNotFound(true);
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const { mutateAsync: checkUserForBulk } = useMutation({
    mutationFn: userCheck,
    onError: (error) => {
      console.error(error);
    },
  });

  const { mutate: saveUser } = useMutation({
    mutationFn: userSave,
    onSuccess: () => {
      refetch();
      setShowToast(true);
      setToastMessage(`New user created: ${name}`);
      setLockAddUser(false);
      setAsurite('');
      setName('');
      setEmplid('');
      setSelectedRole('');
      setSelectedCollege('');
      setSelectedDepartments([]);
      setDarsSelectedColleges([]);
      setNumberOfDepartments(1);
      setNumberOfDarsColleges(1);
      setShowColleges(false);
      setShowDepartments(false);
      setUserNotFound(false);
      setFirstUserFound(false);
    },
    onError: (error) => {
      console.log(error);
      setShowToast(true);
      setToastMessage(`Error creating user: ${name}`);
      setLockAddUser(false);
      setAsurite('');
      setName('');
      setEmplid('');
      setSelectedRole('');
      setSelectedCollege('');
      setSelectedDepartments([]);
      setDarsSelectedColleges([]);
      setNumberOfDepartments(1);
      setNumberOfDarsColleges(1);
      setShowColleges(false);
      setShowDepartments(false);
      setUserNotFound(false);
      setFirstUserFound(false);
    },
  });

  const { mutateAsync: saveUserForBulk } = useMutation({
    mutationFn: userSave,
    onError: (error) => {
      console.error(error);
    },
  });

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
    setSelectedCollege('');
    setNumberOfDepartments(1);
    setSelectedDepartments([]);
    setNumberOfDarsColleges(1);
    setDarsSelectedColleges([]);

    if (
      e.target.value === 'COLLEGE' ||
      e.target.value === 'DEPARTMENT' ||
      e.target.value === 'DARS'
    )
      setShowColleges(true);
    else setShowColleges(false);

    if (e.target.value === 'DEPARTMENT') setShowDepartments(true);
    else setShowDepartments(false);
  };

  const handleCollegeChange = (e) => {
    setSelectedCollege(e.target.value);
    setNumberOfDepartments(1);
    setSelectedDepartments([]);

    if (selectedRole === 'DEPARTMENT') setShowDepartments(true);
    else setShowDepartments(false);
  };

  const handleDarsCollegeChange = (e, index) => {
    const { value } = e.target;
    const updatedUserColleges = [...selectedDarsColleges];

    updatedUserColleges[index] = value;

    setDarsSelectedColleges(updatedUserColleges);
    setNumberOfDepartments(1);
    setSelectedDepartments([]);

    if (selectedRole === 'DEPARTMENT') setShowDepartments(true);
    else setShowDepartments(false);
  };

  const handleAddDarsCollege = () => {
    setNumberOfDarsColleges(numberOfDarsColleges + 1);
    setDarsSelectedColleges([...selectedDarsColleges, '']);
  };

  const handleRemoveDarsCollege = (index) => {
    const updatedUserColleges = [...selectedDarsColleges];

    updatedUserColleges.splice(index, 1);

    setDarsSelectedColleges(updatedUserColleges);
    setNumberOfDarsColleges(numberOfDarsColleges - 1);
  };

  const handleDepartmentChange = (e, index) => {
    const { value } = e.target;
    const updatedUserDepartments = [...selectedDepartments];

    updatedUserDepartments[index] = value;

    setSelectedDepartments(updatedUserDepartments);
  };

  const handleAddDepartment = () => {
    setNumberOfDepartments(numberOfDepartments + 1);
    setSelectedDepartments([...selectedDepartments, '']);
  };

  const handleRemoveDepartment = (index) => {
    const updatedUserDepartments = [...selectedDepartments];

    updatedUserDepartments.splice(index, 1);

    setSelectedDepartments(updatedUserDepartments);
    setNumberOfDepartments(numberOfDepartments - 1);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    const token = await getAccessToken();

    const data = {
      asurite: asurite,
      name: name,
      emplid: emplid,
      roles: JSON.stringify([selectedRole]),
      college:
        selectedRole === 'DARS'
          ? JSON.stringify(selectedDarsColleges.filter((n) => n !== ''))
          : selectedCollege || undefined,
      department: JSON.stringify(selectedDepartments.filter((n) => n !== '')),
    };

    if (asurite && selectedRole) {
      setLockAddUser(true);
      saveUser({ jsonData: data, token });
    } else alert('Please fill out all fields');
  };

  const handleCheckUser = async () => {
    const userFound =
      !asurite || !getAllUsersData
        ? false
        : getAllUsersData.some((user) => {
            if (user.sk.S === asurite) return true;
            const userDetails = !!user?.details?.S
              ? JSON.parse(user.details.S)
              : null;
            return userDetails?.emplid === asurite;
          });

    if (!userFound) {
      const token = await getAccessToken();

      checkUser({ id: asurite, token });
    } else {
      setUserExists(true);
    }
  };

  const handleChangeAsurite = (e) => {
    setAsurite(e.target.value);
    setName('');
    setUserExists(false);
    setUserNotFound(false);
  };

  const handleClearName = () => {
    setName('');
    setAsurite('');
  };

  const handleUpdateUsers = async () => {
    const token = await getAccessToken();

    try {
      const usersWithoutNames = getAllUsersData.filter((user) => {
        const userDetails = !!user?.details?.S
          ? JSON.parse(user.details.S)
          : null;
        return !userDetails?.name || !userDetails?.emplid;
      });

      const promises = usersWithoutNames.map(async (user) => {
        const data = await checkUserForBulk({ id: user.sk.S, token });

        const names = data.owner?.names;
        const ownerEmplid = data.owner?.emplid;
        const nameInfo =
          names && !!Object.keys(names).length ? names.PRF ?? names.PRI : null;

        if (nameInfo && ownerEmplid && user.sk?.S && user.roles?.S) {
          const userName = nameInfo.givenName + ' ' + nameInfo.familyName;

          const data = {
            asurite: user.sk.S,
            name: userName,
            emplid: ownerEmplid,
            roles: user.roles.S,
            college: user.college?.S || undefined,
            department: user.department?.S || '[]',
          };

          saveUserForBulk({ jsonData: data, token });
        } else {
          console.log('User not found for user object:', user);
        }
      });

      await Promise.all(promises);
      refetch();
    } catch (error) {
      console.error('Some mutations failed:', error);
    }
  };

  const handleFilterStatus = (e) => {
    const { value } = e.target;

    // Add or remove the status to or from the list of status filters
    const currentStatuses = statusFilters.includes(value)
      ? statusFilters.filter((status) => status !== value)
      : [...statusFilters, value];

    // Adjust the label based off of the number of selected status filters
    // const newLabel =
    //   currentStatuses.length === 0
    //     ? 'Status'
    //     : currentStatuses.length === 1
    //       ? currentStatuses[0] === 'NONE'
    //         ? 'No checksheet exists'
    //         : StaticData.statusArray[currentStatuses[0]]
    //       : `Status (${currentStatuses.length})`;

    // setStatusLabel(newLabel);
    setStatusFilters(currentStatuses);
  };

  const handleUncheckAllStatuses = () => {
    // setStatusLabel('Status');
    setStatusFilters([]);
  };

  const handleFilterCollege = (e) => {
    const { value } = e.target;

    // Add or remove the status to or from the list of status filters
    const currentColleges = collegeFilters.includes(value)
      ? collegeFilters.filter((status) => status !== value)
      : [...collegeFilters, value];

    // Adjust the label based off of the number of selected status filters
    // const newLabel =
    //   currentColleges.length === 0
    //     ? 'College'
    //     : currentColleges.length === 1
    //       ? currentColleges[0] === 'NONE'
    //         ? 'No checksheet exists'
    //         : StaticData.statusArray[currentColleges[0]]
    //       : `College (${currentColleges.length})`;

    // setCollegeLabel(newLabel);
    setCollegeFilters(currentColleges);
  };

  const handleUncheckAllColleges = () => {
    // setCollegeLabel('College');
    setCollegeFilters([]);
  };

  const handleFilterDepartment = (e) => {
    const { value } = e.target;

    // Add or remove the status to or from the list of status filters
    const currentDeparments = departmentFilters.includes(value)
      ? departmentFilters.filter((status) => status !== value)
      : [...departmentFilters, value];

    // Adjust the label based off of the number of selected status filters
    // const newLabel =
    //   currentDeparments.length === 0
    //     ? 'Department'
    //     : currentDeparments.length === 1
    //       ? currentDeparments[0] === 'NONE'
    //         ? 'No checksheet exists'
    //         : StaticData.statusArray[currentDeparments[0]]
    //       : `Department (${currentDeparments.length})`;

    // setDepartmentLabel(newLabel);
    setDepartmentFilters(currentDeparments);
  };

  const handleUncheckAllDepartments = () => {
    // setDepartmentLabel('Department');
    setDepartmentFilters([]);
  };

  useEffect(() => {
    if (!getAllUsersData) return;

    setFilteredUsers(
      getAllUsersData
        .filter((item) => {
          const searchLower = searchString.toLowerCase();

          // Parse details
          const details = item?.details?.S ? JSON.parse(item.details.S) : null;

          // Check search match for name, asurite, and emplid
          const matchesSearch =
            !searchString ||
            item.sk.S.toLowerCase().includes(searchLower) ||
            details?.name?.toLowerCase().includes(searchLower) ||
            details?.emplid?.toLowerCase().includes(searchLower);

          // Check status filter
          const matchesStatus =
            !statusFilters.length ||
            (item.roles?.S &&
              statusFilters.includes(JSON.parse(item.roles.S)[0]));

          const itemColleges = !!item?.college?.S
            ? item.college.S.includes('[')
              ? JSON.parse(item.college.S)
              : item.college.S !== 'undefined' && [item.college.S]
            : null;

          const matchesCollege =
            !collegeFilters.length ||
            (itemColleges &&
              collegeFilters.some((college) => itemColleges.includes(college)));

          const itemDepartments = !!item?.department?.S
            ? item.department.S.includes('[')
              ? JSON.parse(item.department.S)
              : item.department.S !== 'undefined' && [item.department.S]
            : null;

          const matchesDepartment =
            !departmentFilters.length ||
            (itemDepartments &&
              departmentFilters.some((department) =>
                itemDepartments.includes(department)
              ));

          return (
            matchesSearch &&
            matchesStatus &&
            matchesCollege &&
            matchesDepartment
          );
        })
        .toSorted((a, b) => {
          const aDetails = !!a?.details?.S ? JSON.parse(a.details.S) : null;
          const bDetails = !!b?.details?.S ? JSON.parse(b.details.S) : null;

          const aName = aDetails?.name || a.sk.S;
          const bName = bDetails?.name || b.sk.S;

          return aName.localeCompare(bName);
        })
    );

    // // Check department codes and descriptions
    // if (item.department?.S) {
    //   const departments = item.department.S.includes('[')
    //     ? JSON.parse(item.department.S)
    //     : [item.department.S];

    //   for (const code of departments) {
    //     if (code.toLowerCase().includes(searchLower)) return true;
    //     if (
    //       departmentList[code]?.description
    //         ?.toLowerCase()
    //         .includes(searchLower)
    //     )
    //       return true;
    //   }
    // }
  }, [
    getAllUsersData,
    searchString,
    collegeList,
    departmentList,
    statusFilters,
    collegeFilters,
    departmentFilters,
  ]);

  if (getAllUsersIsPending && getAllUsersFetchStatus === 'fetching')
    return <div className="container">Loading...</div>;

  if (getAllUsersIsError)
    return (
      <div>
        <div>An error has occurred: {getAllUsersError.message}</div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <div className="container position-relative">
        <h2 className="mb-6">Manage users</h2>
        <h3 className="mb-4">Create new user</h3>
        <form
          className="uds-form"
          onSubmit={(e) => {
            handleSaveUser(e);
          }}
        >
          <div className="row mb-2">
            <div className="col">
              <label htmlFor="asurite">Enter ASURITE or emplid</label>
              <div className="d-flex gap-2">
                {!name ? (
                  <div>
                    <input
                      type="text"
                      id="asurite"
                      className={classNames(
                        (userNotFound || userExists) && 'is-invalid',
                        'form-control'
                      )}
                      style={{ width: '385px' }}
                      placeholder="ASURITE or emplid"
                      value={asurite}
                      onChange={(e) => handleChangeAsurite(e)}
                      required
                    />
                    {(userNotFound || userExists) && (
                      <small
                        id="errorHelp"
                        className="form-text invalid-feedback fw-bold"
                      >
                        <span
                          title="Alert"
                          className="fa fa-icon fa-exclamation-triangle"
                        ></span>{' '}
                        {userNotFound
                          ? 'No user found'
                          : userExists
                            ? 'User already exists'
                            : null}
                      </small>
                    )}
                  </div>
                ) : (
                  <div
                    className="d-flex justify-content-between align-items-center border border-gray-3"
                    style={{
                      padding: '0.5rem 10px',
                      height: '42px',
                      width: '385px',
                      fontSize: '14px',
                    }}
                  >
                    <div
                      className="bg-gray-2 rounded"
                      style={{ padding: '0 0.5rem' }}
                    >
                      <span className="fw-bold">{name}</span> ({asurite})
                    </div>
                    <button
                      className="bg-transparent border-0 p-0"
                      onClick={handleClearName}
                    >
                      <FontAwesomeIcon icon={faXmark} width={10} height={10} />
                    </button>
                  </div>
                )}
                <div style={{ marginTop: '4px' }}>
                  <Button
                    label="Find user"
                    size="small"
                    color="maroon"
                    onClick={handleCheckUser}
                    disabled={!asurite || userExists}
                  />
                </div>
              </div>
            </div>
          </div>
          {firstUserFound && (
            <>
              <div className="row mb-4">
                <div className="col">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    className="form-control"
                    value={selectedRole}
                    onChange={handleRoleChange}
                    required
                  >
                    <option value="" disabled>
                      Select a Role
                    </option>
                    {StaticData.userRoles.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col">
                  {showColleges && (
                    <div>
                      <label htmlFor="college">
                        {selectedRole === 'DARS'
                          ? 'Colleges/schools'
                          : 'College/school'}
                      </label>
                      {selectedRole === 'DARS' ? (
                        <>
                          {[...Array(numberOfDarsColleges)].map(
                            (selectable, index) => (
                              <div
                                key={index}
                                className="d-flex gap-1 align-items-center mb-1"
                              >
                                <select
                                  id="college"
                                  className="form-control"
                                  value={selectedDarsColleges[index]}
                                  onChange={(e) =>
                                    handleDarsCollegeChange(e, index)
                                  }
                                >
                                  <option value="">
                                    Select a college/school
                                  </option>
                                  {Object.entries(collegeList)
                                    .filter(
                                      ([code, obj]) =>
                                        code !== 'COE' &&
                                        code !== 'CGC' &&
                                        code !== 'CLW' &&
                                        code !== 'CTPH' &&
                                        code !== 'CPROVOST'
                                    )
                                    .filter(
                                      ([code, _]) =>
                                        !selectedDarsColleges.includes(code) ||
                                        selectedDarsColleges[index] === code
                                    )
                                    .toSorted(([aCode, aObj], [bCode, bObj]) =>
                                      aObj.alphaSortDescription.localeCompare(
                                        bObj.alphaSortDescription
                                      )
                                    )
                                    .map(([code, obj]) => (
                                      <option key={code} value={code}>
                                        {obj.alphaSortDescription}
                                      </option>
                                    ))}
                                </select>
                                {numberOfDarsColleges > 1 && (
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() =>
                                      handleRemoveDarsCollege(index)
                                    }
                                  />
                                )}
                              </div>
                            )
                          )}
                          <div
                            className="d-flex gap-1 align-items-center text-maroon underline-hover"
                            onClick={handleAddDarsCollege}
                          >
                            <FontAwesomeIcon icon={faPlusCircle} />
                            Add additional college
                          </div>
                        </>
                      ) : (
                        <select
                          id="college"
                          className="form-control"
                          value={selectedCollege}
                          onChange={(e) => handleCollegeChange(e)}
                        >
                          <option value="">Select a College</option>
                          {Object.entries(collegeList)
                            .filter(
                              ([code, obj]) =>
                                code !== 'COE' &&
                                code !== 'CGC' &&
                                code !== 'CLW' &&
                                code !== 'CTPH' &&
                                code !== 'CPROVOST'
                            )
                            .toSorted(([aCode, aObj], [bCode, bObj]) =>
                              aObj.alphaSortDescription.localeCompare(
                                bObj.alphaSortDescription
                              )
                            )
                            .map(([code, obj]) => (
                              <option key={code} value={code}>
                                {obj.alphaSortDescription}
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>

                <div className="col">
                  {showDepartments && (
                    <div>
                      <label htmlFor="department">Departments</label>
                      {[...Array(numberOfDepartments)].map(
                        (selectable, index) => (
                          <div
                            key={index}
                            className="d-flex gap-1 align-items-center mb-1"
                          >
                            <select
                              id="department"
                              className="form-control"
                              value={selectedDepartments[index]}
                              onChange={(e) => handleDepartmentChange(e, index)}
                            >
                              <option value="">Select a Department</option>
                              {!!selectedCollege &&
                                !!collegeList[selectedCollege]?.departments &&
                                Object.entries(
                                  collegeList[selectedCollege].departments
                                )
                                  .filter(
                                    ([code, _]) =>
                                      !selectedDepartments.includes(code) ||
                                      selectedDepartments[index] === code
                                  )
                                  .toSorted(([aCode, aObj], [bCode, bObj]) =>
                                    aObj.description.localeCompare(
                                      bObj.description
                                    )
                                  )
                                  .map(([code, obj]) => (
                                    <option key={code} value={code}>
                                      {obj.description}
                                    </option>
                                  ))}
                            </select>
                            {numberOfDepartments > 1 && (
                              <FontAwesomeIcon
                                icon={faTrash}
                                onClick={() => handleRemoveDepartment(index)}
                              />
                            )}
                          </div>
                        )
                      )}
                      {!!selectedCollege &&
                        !!collegeList[selectedCollege]?.departments &&
                        Object.entries(collegeList[selectedCollege].departments)
                          .length !== numberOfDepartments && (
                          <div
                            className="d-flex gap-1 align-items-center text-maroon underline-hover"
                            onClick={handleAddDepartment}
                          >
                            <FontAwesomeIcon icon={faPlusCircle} />
                            Add additional department
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
              <div className="row mb-5">
                <div className="col">
                  <Button
                    label="Add new user"
                    color="gold"
                    icon={['fas', 'plus']}
                    disabled={
                      lockAddUser ||
                      !asurite ||
                      !name ||
                      !selectedRole ||
                      (selectedRole === 'COLLEGE' && !selectedCollege) ||
                      (selectedRole === 'DEPARTMENT' &&
                        (selectedDepartments.length === 0 ||
                          !selectedDepartments[0])) ||
                      (selectedRole === 'DARS' &&
                        (selectedDarsColleges.length === 0 ||
                          !selectedDarsColleges[0])) ||
                      userExists
                    }
                  />
                </div>
              </div>
            </>
          )}
        </form>
        <hr />
        {getAllUsersIsSuccess && (
          <div className="row">
            <div className="col">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-4 align-items-center">
                  <div className="d-flex gap-4 align-items-center">
                    <h3>All users</h3>
                  </div>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="pe-4"
                      style={{ width: '280px' }}
                      value={searchString}
                      onChange={(e) => setSearchString(e.target.value)}
                      placeholder="Search users"
                    />
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      className="position-absolute top-0 end-0 text-gray-5"
                      style={{ margin: '0.7rem' }}
                    />
                  </div>
                  <div className="filters d-flex gap-2 align-items-center">
                    <Dropdown>
                      <Dropdown.Toggle
                        as={DropdownCustomToggle}
                        id="dropdown-custom-components"
                      >
                        <Button
                          label={'Role'}
                          icon={['fa', 'chevron-down']}
                          classes={['btn', 'btn-white', 'btn-md']}
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="py-0"
                        style={{ width: '340px' }}
                      >
                        <Dropdown.Item
                          className="button-link px-3 mt-2"
                          role="button"
                          onClick={handleUncheckAllStatuses}
                        >
                          Uncheck all
                        </Dropdown.Item>
                        {StaticData.userRoles.map((status) => (
                          <Dropdown.ItemText
                            key={status.value}
                            className="d-flex gap-2 ps-3"
                            role="button"
                          >
                            <input
                              type="checkbox"
                              id={'checkbox-' + status.value}
                              value={status.value}
                              checked={statusFilters.includes(status.value)}
                              onChange={(e) => handleFilterStatus(e)}
                            />
                            <span>{status.label}</span>
                          </Dropdown.ItemText>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Toggle
                        as={DropdownCustomToggle}
                        id="dropdown-custom-components"
                      >
                        <Button
                          label={'College'}
                          icon={['fa', 'chevron-down']}
                          classes={[
                            'btn',
                            collegeFilters.length > 0
                              ? 'btn-maroon'
                              : 'btn-white',
                            'btn-md',
                          ]}
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="py-0"
                        style={{ width: '540px' }}
                      >
                        <Dropdown.Item
                          className="button-link px-3 mt-2"
                          role="button"
                          onClick={handleUncheckAllColleges}
                        >
                          Uncheck all
                        </Dropdown.Item>
                        {Object.entries(collegeList)
                          .filter(
                            ([code, obj]) =>
                              code !== 'COE' &&
                              code !== 'CGC' &&
                              code !== 'CLW' &&
                              code !== 'CTPH' &&
                              code !== 'CPROVOST'
                          )
                          .toSorted(([aCode, aObj], [bCode, bObj]) =>
                            aObj.alphaSortDescription.localeCompare(
                              bObj.alphaSortDescription
                            )
                          )
                          .map(([code, obj]) => (
                            <Dropdown.ItemText
                              key={code}
                              className="d-flex gap-2 ps-3"
                              role="button"
                            >
                              <input
                                type="checkbox"
                                id={'checkbox-' + code}
                                value={code}
                                checked={collegeFilters.includes(code)}
                                onChange={(e) => handleFilterCollege(e)}
                              />
                              <span>{obj.alphaSortDescription}</span>
                            </Dropdown.ItemText>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Toggle
                        as={DropdownCustomToggle}
                        id="dropdown-custom-components"
                      >
                        <Button
                          label={'Department'}
                          icon={['fa', 'chevron-down']}
                          classes={[
                            'btn',
                            departmentFilters.length > 0
                              ? 'btn-maroon'
                              : 'btn-white',
                            'btn-md',
                          ]}
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        className="py-0"
                        style={{ width: '540px' }}
                      >
                        <Dropdown.Item
                          className="button-link px-3 mt-2"
                          role="button"
                          onClick={handleUncheckAllDepartments}
                        >
                          Uncheck all
                        </Dropdown.Item>
                        {Object.entries(departmentList)
                          .toSorted(([aCode, aObj], [bCode, bObj]) =>
                            aObj.description.localeCompare(bObj.description)
                          )
                          .map(([code, obj]) => (
                            <Dropdown.ItemText
                              key={code}
                              className="d-flex gap-2 ps-3"
                              role="button"
                            >
                              <input
                                type="checkbox"
                                id={'checkbox-' + code}
                                value={code}
                                checked={departmentFilters.includes(code)}
                                onChange={(e) => handleFilterDepartment(e)}
                              />
                              <span>{obj.description}</span>
                            </Dropdown.ItemText>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-center">
                  <div className="fst-italic">
                    {filteredUsers.length} users found
                  </div>
                  <Button
                    label="Check all users"
                    size="small"
                    onClick={handleUpdateUsers}
                  />
                </div>
              </div>
              <table className="table">
                <thead>
                  <tr className="users">
                    <th>Name</th>
                    <th className="text-nowrap">ASURITE ID</th>
                    <th>Role</th>
                    <th>Colleges/schools</th>
                    <th>Departments</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!!filteredUsers.length ? (
                    filteredUsers.map((item, i) => (
                      <UserRow
                        key={i}
                        item={item}
                        collegeList={collegeList}
                        departmentList={departmentList}
                        refetch={refetch}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-gray-6 fst-italic">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <ToastContainer
          position="top-end"
          containerPosition="fixed"
          className="altered-position"
        >
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={5000}
            autohide
          >
            <Toast.Body className="p-2 bg-white">
              <div className="d-flex gap-2 justify-items-between align-items-center">
                <span className="text-nowrap">{toastMessage}</span>
                <button
                  className="bg-transparent border-0 p-0"
                  onClick={() => setShowToast(false)}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    width={16}
                    height={16}
                    className="text-gray-6"
                  />
                </button>
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
    </>
  );
};

const UserRow = ({
  item,
  collegeList,
  departmentList,
  refetch,
  setShowToast,
  setToastMessage,
}) => {
  const { getAccessToken } = useServiceAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedRole, setEditedRole] = useState('');
  const [editedCollege, setEditedCollege] = useState('');
  const [editedDarsColleges, setEditedDarsColleges] = useState([]);
  const [editedDepartments, setEditedDepartments] = useState([]);
  const [numberOfDarsColleges, setNumberOfDarsColleges] = useState(1);
  const [numberOfDepartments, setNumberOfDepartments] = useState(1);

  const userCollege = useMemo(
    () =>
      !!item?.college?.S
        ? item.college.S.includes('[')
          ? JSON.parse(item.college.S)
          : item.college.S !== 'undefined' && [item.college.S]
        : null,
    [item?.college?.S]
  );

  const userDepartment = useMemo(
    () =>
      !!item?.department?.S
        ? item.department.S.includes('[')
          ? JSON.parse(item.department.S)
          : item.department.S !== 'undefined' && [item.department.S]
        : null,
    [item?.department?.S]
  );

  const details = useMemo(
    () => (!!item?.details?.S ? JSON.parse(item.details.S) : null),
    [item?.details?.S]
  );

  const { mutate: deleteUser } = useMutation({
    mutationFn: userDelete,
    onSuccess: () => {
      refetch();
      setShowToast(true);
      setToastMessage(`User deleted: ${details.name}`);
    },
  });

  const { mutate: saveUser } = useMutation({
    mutationFn: userSave,
    onSuccess: () => {
      refetch();
      setShowToast(true);
      setToastMessage(`User updated: ${details.name}`);
      setIsEditing(false);
    },
    onError: (error) => {
      console.log(error);
      setShowToast(true);
      setToastMessage(`Error updating user: ${details.name}`);
      setIsEditing(false);
    },
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleDeleteUser = async () => {
    const token = await getAccessToken();

    if (window.confirm(`Are you sure you want to delete user ${item.sk.S}?`)) {
      setIsEditing(false);
      deleteUser({ id: item.sk.S, token });
    }
  };

  const handleSaveUser = async () => {
    const token = await getAccessToken();
    const noCollegeDeptRoles = ['VIEWER', 'PROVOST', 'PRVVIEWER'].includes(
      editedRole
    );

    const data = {
      asurite: item.sk.S,
      name: details?.name,
      emplid: details?.emplid,
      roles: JSON.stringify([editedRole]),
      college: noCollegeDeptRoles
        ? undefined // or "" if your API prefers an empty string
        : editedRole === 'DARS'
          ? JSON.stringify(editedDarsColleges.filter((n) => n !== ''))
          : editedCollege || undefined,
      department: noCollegeDeptRoles
        ? '[]' // consistent with your stringified array usage
        : JSON.stringify(editedDepartments.filter((n) => n !== '')),
    };

    saveUser({ jsonData: data, token });
  };

  const handleDarsCollegeChange = (e, index) => {
    const { value } = e.target;
    const updatedColleges = [...editedDarsColleges];
    updatedColleges[index] = value;
    setEditedDarsColleges(updatedColleges);
  };

  const handleAddDarsCollege = () => {
    setNumberOfDarsColleges(numberOfDarsColleges + 1);
    setEditedDarsColleges([...editedDarsColleges, '']);
  };

  const handleRemoveDarsCollege = (index) => {
    const updatedColleges = [...editedDarsColleges];
    updatedColleges.splice(index, 1);
    setEditedDarsColleges(updatedColleges);
    setNumberOfDarsColleges(numberOfDarsColleges - 1);
  };

  const handleDepartmentChange = (e, index) => {
    const { value } = e.target;
    const updatedDepartments = [...editedDepartments];
    updatedDepartments[index] = value;
    setEditedDepartments(updatedDepartments);
  };

  const handleAddDepartment = () => {
    setNumberOfDepartments(numberOfDepartments + 1);
    setEditedDepartments([...editedDepartments, '']);
  };

  const handleRemoveDepartment = (index) => {
    const updatedDepartments = [...editedDepartments];
    updatedDepartments.splice(index, 1);
    setEditedDepartments(updatedDepartments);
    setNumberOfDepartments(numberOfDepartments - 1);
  };

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setEditedRole(role);

    // Reset college and department selections when role changes
    if (role !== 'DARS') {
      setEditedDarsColleges([]);
      setNumberOfDarsColleges(1);
    }

    if (role !== 'DEPARTMENT') {
      setEditedDepartments([]);
      setNumberOfDepartments(1);
    }
  };

  // Initialize edit form with current values when edit button is clicked
  useEffect(() => {
    if (isEditing) {
      // Parse role from string format
      const roleString = item?.roles?.S || '';
      const parsedRole = roleString.includes('[')
        ? JSON.parse(roleString)[0]
        : roleString;
      setEditedRole(parsedRole);

      // Parse college(s)
      if (item?.college?.S) {
        if (parsedRole === 'DARS') {
          const colleges = item.college.S.includes('[')
            ? JSON.parse(item.college.S)
            : [item.college.S];
          setEditedDarsColleges(colleges);
          setNumberOfDarsColleges(colleges.length);
        } else {
          setEditedCollege(
            item.college.S !== 'undefined' ? item.college.S : ''
          );
        }
      }

      // Parse departments
      if (item?.department?.S) {
        const departments = item.department.S.includes('[')
          ? JSON.parse(item.department.S)
          : [item.department.S];
        setEditedDepartments(departments.filter((d) => d !== 'undefined'));
        setNumberOfDepartments(
          departments.filter((d) => d !== 'undefined').length || 1
        );
      }
    }
  }, [isEditing, item]);

  return (
    <>
      <tr className="user">
        <td>
          <div className="text-nowrap fw-bold">{details?.name ?? '\u2014'}</div>
        </td>
        <td>{item.sk.S}</td>
        <td>{!!item?.roles?.S && roleLabel[item.roles.S]}</td>
        <td>
          {!!userCollege?.length && !!collegeList ? (
            <ul
              className={classNames(
                userCollege.length > 1 ? 'ps-3' : 'list-unstyled',
                'm-0'
              )}
            >
              {userCollege.map(
                (coll, index) =>
                  !!coll && (
                    <li key={index}>
                      {!!collegeList[coll]
                        ? collegeList[coll].description
                        : '{College not found}'}{' '}
                      ({coll})
                    </li>
                  )
              )}
            </ul>
          ) : (
            <span className="text-gray-4">{'\u2014'}</span>
          )}
        </td>
        <td>
          {!!userDepartment?.length && !!departmentList ? (
            <ul
              className={classNames(
                userDepartment.length > 1 ? 'ps-3' : 'list-unstyled',
                'm-0'
              )}
            >
              {userDepartment.map(
                (dept, index) =>
                  !!dept && (
                    <li key={index}>
                      {!!departmentList[dept]
                        ? departmentList[dept].description
                        : '{Department not found}'}{' '}
                      ({dept})
                    </li>
                  )
              )}
            </ul>
          ) : (
            <span className="text-gray-4">{'\u2014'}</span>
          )}
        </td>
        <td>
          {item.sk.S !== 'lcabre' && (
            <div className="d-flex gap-2 justify-content-end align-items-center">
              <Button label="Edit" size="xsmall" onClick={handleEditClick} />
              <button
                className="bg-transparent border-0 p-0 text-gray-7"
                onClick={handleDeleteUser}
              >
                <FontAwesomeIcon icon={faTrash} width={14} height={14} />
              </button>
            </div>
          )}
        </td>
      </tr>

      <Modal show={isEditing} onHide={() => setIsEditing(false)}>
        <div className="p-4" style={{ width: '640px' }}>
          <h3 className="mb-4">Edit user</h3>
          <h4 style={{ marginLeft: '3px' }}>
            <span className="highlight-gold" style={{ padding: '0.25rem' }}>
              {details?.name}
            </span>
          </h4>
          <div className="mb-4">
            <div>ASURITE ID: {item.sk.S}</div>
            <div>ASU Emplid: {details?.emplid}</div>
          </div>
          <div>
            <div className="mb-4">
              <div className="fw-bold">Role</div>
              <select
                className="form-control"
                value={editedRole}
                onChange={handleRoleChange}
              >
                <option value="" disabled>
                  Select a Role
                </option>
                {StaticData.userRoles.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {['DARS', 'COLLEGE', 'DEPARTMENT'].includes(editedRole) && (
              <div className="mb-4">
                <div className="fw-bold">
                  {editedRole === 'DARS'
                    ? 'Colleges/schools'
                    : 'College/school'}
                </div>
                {editedRole === 'DARS' ? (
                  <div>
                    {[...Array(numberOfDarsColleges)].map((_, index) => (
                      <div
                        key={index}
                        className="d-flex gap-1 align-items-center mb-1"
                      >
                        <select
                          className="form-control"
                          value={editedDarsColleges[index] || ''}
                          onChange={(e) => handleDarsCollegeChange(e, index)}
                        >
                          <option value="">Select a College</option>
                          {Object.entries(collegeList)
                            .filter(
                              ([code, obj]) =>
                                code !== 'COE' &&
                                code !== 'CGC' &&
                                code !== 'CLW' &&
                                code !== 'CTPH' &&
                                code !== 'CPROVOST'
                            )
                            .filter(
                              ([code, _]) =>
                                !editedDarsColleges.includes(code) ||
                                editedDarsColleges[index] === code
                            )
                            .toSorted(([aCode, aObj], [bCode, bObj]) =>
                              aObj.alphaSortDescription.localeCompare(
                                bObj.alphaSortDescription
                              )
                            )
                            .map(([code, obj]) => (
                              <option key={code} value={code}>
                                {obj.alphaSortDescription}
                              </option>
                            ))}
                        </select>
                        {numberOfDarsColleges > 1 && (
                          <FontAwesomeIcon
                            icon={faTrash}
                            onClick={() => handleRemoveDarsCollege(index)}
                          />
                        )}
                      </div>
                    ))}
                    <div
                      className="d-flex gap-1 align-items-center text-maroon underline-hover"
                      onClick={handleAddDarsCollege}
                    >
                      <FontAwesomeIcon icon={faPlusCircle} />
                      Add additional college
                    </div>
                  </div>
                ) : (
                  ['COLLEGE', 'DEPARTMENT'].includes(editedRole) && (
                    <select
                      className="form-control"
                      value={editedCollege}
                      onChange={(e) => setEditedCollege(e.target.value)}
                    >
                      <option value="">Select a College</option>
                      {Object.entries(collegeList)
                        .filter(
                          ([code, obj]) =>
                            code !== 'COE' &&
                            code !== 'CGC' &&
                            code !== 'CLW' &&
                            code !== 'CTPH' &&
                            code !== 'CPROVOST'
                        )
                        .toSorted(([aCode, aObj], [bCode, bObj]) =>
                          aObj.alphaSortDescription.localeCompare(
                            bObj.alphaSortDescription
                          )
                        )
                        .map(([code, obj]) => (
                          <option key={code} value={code}>
                            {obj.alphaSortDescription}
                          </option>
                        ))}
                    </select>
                  )
                )}
              </div>
            )}
            {editedRole === 'DEPARTMENT' && (
              <div className="mb-4">
                <div className="fw-bold">Departments</div>
                <div>
                  {[...Array(numberOfDepartments)].map((_, index) => (
                    <div
                      key={index}
                      className="d-flex gap-2 align-items-center mb-1"
                    >
                      <select
                        className="form-control"
                        value={editedDepartments[index] || ''}
                        onChange={(e) => handleDepartmentChange(e, index)}
                      >
                        <option value="">Select a Department</option>
                        {!!editedCollege &&
                          !!collegeList[editedCollege]?.departments &&
                          Object.entries(collegeList[editedCollege].departments)
                            .filter(
                              ([code, _]) =>
                                !editedDepartments.includes(code) ||
                                editedDepartments[index] === code
                            )
                            .toSorted(([aCode, aObj], [bCode, bObj]) =>
                              aObj.description.localeCompare(bObj.description)
                            )
                            .map(([code, obj]) => (
                              <option key={code} value={code}>
                                {obj.description}
                              </option>
                            ))}
                      </select>
                      {numberOfDepartments > 1 && (
                        <FontAwesomeIcon
                          icon={faTrash}
                          onClick={() => handleRemoveDepartment(index)}
                        />
                      )}
                    </div>
                  ))}
                  {!!editedCollege &&
                    !!collegeList[editedCollege]?.departments &&
                    Object.entries(collegeList[editedCollege].departments)
                      .length !== numberOfDepartments && (
                      <button
                        className="bg-transparent border-0 p-0 text-maroon button-link"
                        onClick={handleAddDepartment}
                      >
                        <FontAwesomeIcon icon={faPlusCircle} />
                        <span className="ms-1">Add additional department</span>
                      </button>
                    )}
                </div>
              </div>
            )}
            <div className="d-flex gap-2 justify-content-between align-items-center">
              <div className="d-flex gap-2">
                <Button
                  label="Apply changes"
                  color="gold"
                  onClick={handleSaveUser}
                />
                <Button label="Cancel" onClick={() => setIsEditing(false)} />
              </div>
              <button
                className="bg-transparent border-0 p-0 button-link"
                onClick={handleDeleteUser}
              >
                Delete user
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Users;
